import { Button } from '@progress/kendo-react-buttons'
import { DatePicker, DateRangePicker } from '@progress/kendo-react-dateinputs'
import { Grid, GridColumn, GridPageChangeEvent, GridSortChangeEvent } from '@progress/kendo-react-grid'
import over from 'lodash/fp/over'
import pipe from 'lodash/fp/pipe'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import once from 'lodash/once'
import moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'
import { Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap'
import * as Redux from 'redux'
import * as App from '../../app/types'
import * as Breadcrumb from '../../components/breadcrumbs'
import { FlashMessage } from '../../components/flash-message'
import * as Form from '../../components/form'
import * as ImageUpload from '../../components/image-upload'
import * as IsActive from '../../components/is_active'
import * as Link from '../../components/link'
import { Loading } from '../../components/loading'
import * as Menu from '../../components/menu/left-nav'
import * as RasaModal from '../../components/modal'
import * as Pager from '../../components/pager'
import * as RasaInput from '../../components/rasa-input'
import * as Sortable from '../../components/sortable'
import * as Request from '../../request'
import { ApplicationState } from '../../rootReducer';
import * as Utils from '../../utils'
import * as GridCell from '../grid-cell'
import * as PostsUtils from '../posts/utils'
import * as Reference from '../reference'
import * as Actions from './actions'
import * as Constants from './constants'
import * as Render from './render'
import './styles.css'
import * as Types from './types'

const breadcrumbs: Breadcrumb.Breadcrumbs = Menu.breadcrumbs(Menu.CONTENT_LEAD_POSTS_URL)

interface AppProps {
  app: App.State
  leadPosts: Types.FullComponentState
  referenceData?: Reference.CategoryProps
}

interface ModalDispatchProps {
  closeModal: RasaModal.Toggle,
  onSubmit: Redux.ActionCreator<Form.SaveAction<Types.FormFields>>,
  updateDate: any, // TODO: I am so sick of writing types right now. Please forgive me.
  updateEndDate: any,
  updateField: Redux.ActionCreator<Form.UpdateFieldAction>,
}

interface UploadImageProps {
  uploadImage: any;
}
type ModalProps = AppProps & ModalDispatchProps & UploadImageProps

const {FormValidators: validators, FormFieldNames: fieldNames} = Constants

const LeadPostModal = ({app, leadPosts, onSubmit, closeModal, referenceData,
  updateDate, updateEndDate, updateField}: ModalProps) => {
  const isEditingOldPost = leadPosts.form.fields.id && leadPosts.form.fields.start_date < new Date()
  return <Modal className="modal-lg" isOpen={leadPosts.modal.isOpen}>
    <form onSubmit={Link.preventDefault()}>
      <ModalHeader toggle={closeModal}>
        {leadPosts.form.fields.id  ? 'Edit Lead Post' : 'Add Lead Post'}
      </ModalHeader>
      <ModalBody>
        <div className="field">
          <RasaInput.RasaInput label="URL"
                                placeholder=""
                                required
                                helpType="lead-post-url"
                                value={leadPosts.form.fields.url || ''}
                                disabled={leadPosts.form.fields.id > 0}
                                validation={validators[fieldNames.Url](leadPosts.form.fields.url)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  updateField({name: fieldNames.Url, value: e.target.value})} />
        </div>
        {leadPosts.modal.retrieved_url &&
        <div className="field field-notice">
          <p>
            We retrieved a different url than the one you submitted, please select which one you'd like to use:
          </p>
          <RasaInput.RasaSelectInput
              label=""
              // validation={validators[fieldNames.FeatureType](leadPosts.form.fields.feature_type)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField({name: fieldNames.Url, value: e.target.value})}
              options={[
                {key: leadPosts.modal.original_url, description: 'I want exactly the url I submitted'},
                {key: leadPosts.modal.retrieved_url, description: 'I want the one you retrieved'},
              ]}
              value={leadPosts.form.fields.url || 'select'} />
        </div> }
        <div className="field">
          <RasaInput.RasaInput label="Title"
                                helpType="lead-post-title"
                                value={leadPosts.form.fields.title || ''}
                                disabled={isEditingOldPost}
                                validation={
                                  leadPosts.form.fields.id ? RasaInput.validatePresent(leadPosts.form.fields.title)
                                  : {
                                    invalid: leadPosts.modal.data.showAddValidation
                                      && leadPosts.form.fields.title === '',
                                  }
                                }
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  updateField({name: fieldNames.Title, value: e.target.value})} />
        </div>
        <div className="source-and-rank-box">
          <div className="field source-container">
            <RasaInput.RasaInput label="Source"
              helpType="lead-post-site-name"
              value={leadPosts.form.fields.site_name || ''}
              disabled={isEditingOldPost}
              validation={
                leadPosts.form.fields.id ? RasaInput.validatePresent(leadPosts.form.fields.site_name)
                : {
                  invalid: leadPosts.modal.data.showAddValidation
                    && leadPosts.form.fields.site_name === '',
                }
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField({name: fieldNames.SiteName, value: e.target.value})} />
          </div>
          <div className="field rank-container">
            <RasaInput.RasaNumericInput label="Rank"
                                        helpType="lead-post-rank"
                                        value={leadPosts.form.fields.rank || ''}
                                        disabled={isEditingOldPost}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                          updateField({
                                            name: fieldNames.Rank,
                                            value: isEmpty(e.target.value) ? '0' : e.target.value})
                                        }/>
            </div>
        </div>
        <div className="field">
          <RasaInput.RasaInput label="Description"
                                type="textarea"
                                helpType="lead-post-description"
                                value={leadPosts.form.fields.description || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  updateField({name: fieldNames.Description, value: e.target.value})} />
        </div>
        <div className="image-box">
          <div className="field image-url-and-date-container">
            <div className="field image-url-container">
              <ImageUpload.ImageUpload imageUrl={leadPosts.form.fields.image_url}
                fieldChangeAction={(fieldValue: string) => updateField({name: fieldNames.ImageUrl, value: fieldValue})}
              />
            </div>
            <div className="field date-container">
              {isEditingOldPost
              ?
              <div>
                <Label className="field-label">Date Range</Label>
                <div className="start-end-container">
                  <div>
                    <span>start</span>
                    <DatePicker
                      disabled={true}
                      value={isNil(leadPosts.form.fields.start_date) ? null : leadPosts.form.fields.start_date.toDate()}
                    />
                  </div>
                  <div>
                    <span>end</span>
                    <DatePicker
                      value={isNil(leadPosts.form.fields.end_date) ? null : leadPosts.form.fields.end_date.toDate()}
                      onChange={updateEndDate}
                    />
                  </div>
                </div>
              </div>
              :
              <div>
                <Label className="field-label">Date Range</Label>
                <DateRangePicker
                  value={{
                    end: isNil(leadPosts.form.fields.end_date) ? null :
                      leadPosts.form.fields.end_date.toDate(),
                    start: isNil(leadPosts.form.fields.start_date) ? null :
                      leadPosts.form.fields.start_date.toDate(),
                  }}
                  onChange={updateDate}
                />
              </div>
              }

            </div>
          </div>
          {app.viewport.isLarge &&
          <div className="edit-preview-image-container">
            <img
            onError={once((e) =>
              e.currentTarget.src = 'https://image-api.rasa.io/image/rasa-templates/path/wireframe-placeholder.png')}
            className="edit-preview-image" src={leadPosts.form.fields.image_url}/>
          </div>}
        </div>
        <div className="field post-type">
        <Row>
          <Col>
            <RasaInput.RasaSelectInput
              label="Post Type"
              disabled={isEditingOldPost}
              validation={validators[fieldNames.FeatureType](leadPosts.form.fields.feature_type)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField({name: fieldNames.FeatureType, value: e.target.value})}
              options={[
                {key: Constants.FeatureTypes.Lead, description: 'Lead'},
                {key: Constants.FeatureTypes.Sponsored, description: 'Sponsored'},
              ]}
              value={leadPosts.form.fields.feature_type || ''} />
          </Col>
          <Col>
            <RasaInput.RasaSelectInput label="Content Category"
                              value={leadPosts.form.fields.category_type_id || ''}
                              disabled={leadPosts.form.fields.id > 0}
                              options={Reference.dropdownTypesById(
                                referenceData.categories || Reference.SUBSCRIPTION_CATEGORIES)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateField({name: fieldNames.CategoryTypeId, value: e.target.value})}/>
          </Col>
        </Row>
        </div>
      </ModalBody>
      <ModalFooter>
        {leadPosts.modal.retrieved_url &&
          <div className="field-notice">
            Please make url selection above!
          </div>}
        {leadPosts.form.isSaving && <Loading size={Loading.Sizes.SMALL} />}
        <Button primary look="outline"
                        onClick={Link.preventDefaultThen(() => onSubmit(
                          leadPosts.form.fields.id ? {
                            ...Form.modifiedFields(leadPosts.form),
                            id: leadPosts.form.fields.id,
                            override_url: leadPosts.modal.retrieved_url ? true : false,
                          } :
                          {
                            ...leadPosts.form.fields,
                            category_type_id: leadPosts.form.fields.category_type_id,
                            end_date: leadPosts.form.fields.end_date,
                            feature_type: leadPosts.form.fields.feature_type,
                            is_active: true,
                            override_url: leadPosts.modal.retrieved_url ? true : false,
                            start_date: leadPosts.form.fields.start_date,
                          }))}
                        disabled={!leadPosts.form.isDirty || leadPosts.form.isSaving || !leadPosts.form.isValid}>
          Save
        </Button>
        <Button look="outline" onClick={closeModal}>Cancel</Button>
      </ModalFooter>
    </form>
  </Modal>
}

export const LeadPostModalConnect = connect(
  ({app, leadPosts, referenceData}: AppProps) => ({app, leadPosts, referenceData}),
  {
    closeModal: Actions.closeModal,
    onSubmit: Actions.FormActions[Form.SAVE],
    updateField: Actions.FormActions[Form.UPDATE],
    uploadImage: Actions.uploadImage,
  },
  (stateProps, dispatchProps) => ({
    ...stateProps,
    ...dispatchProps,
    updateDate: pipe(
      Utils.selectValue,
      over([
        (date) => dispatchProps.updateField({
          name: Constants.FormFieldNames.StartDate,
          value: moment(date.start),
        }),
        (date) => dispatchProps.updateField({
          name: Constants.FormFieldNames.EndDate,
          value: date.end ? moment(date.end) : null,
        }),
      ]),
    ),
    updateEndDate: pipe(
      Utils.selectValue,
      over([
        (date) => dispatchProps.updateField({
          name: Constants.FormFieldNames.EndDate,
          value: date ? moment(date) : null,
        }),
      ]),
    ),
  }),
)(LeadPostModal)

interface AddLeadPostProps { onOpen: Actions.OpenModalAction }

const AddLeadPost = ({leadPosts, onOpen}: Types.WrappedState & AddLeadPostProps) => <span>
  <Button
    disabled={leadPosts.view.isLoading}
    className="secondary-button"
    onClick={onOpen}
    title="Add Lead Post">
    Add
  </Button>
  <LeadPostModalConnect />
</span>

export const AddLeadPostConnected = connect(
  ({leadPosts}: Types.WrappedState) => ({leadPosts}),
  { onOpen: Actions.onOpen },
)(AddLeadPost)

interface ReorderLeadPostProps {
  calendarDate: any,
  onSaveReorder: any,
  onResetReorder: any,
}
const ReorderLeadPost = (
  {app, calendarDate, leadPosts, onSaveReorder, onResetReorder}: AppProps & ReorderLeadPostProps) => <span>
    { leadPosts.view.isSaving
    ?
      <Loading size="small"/>
    :
      <span>
        <Button
          disabled={leadPosts.view.isLoading || !leadPosts.view.isDragged}
          className="secondary-button"
          onClick={() => onSaveReorder(app.selectedCommunity, leadPosts, calendarDate)}
          title="Save Ordered Lead Post">
          Save Ordering
        </Button>
        <Button
          disabled={leadPosts.view.isLoading || !leadPosts.view.isDragged}
          className="secondary-button"
          onClick={onResetReorder}
          title="Reset Lead Post ordering">
          Reset Ordering
        </Button>
      </span>}
  </span>

export const ReorderLeadPostConnected = connect(
  ({app, contentCalendar, leadPosts}: ApplicationState) =>
    ({app, calendarDate: contentCalendar.data.selectedDate, leadPosts}),
  {
    onResetReorder: Actions.resetReorder,
    onSaveReorder: Actions.saveReorder,
  },
)(ReorderLeadPost)

const LeadPostHeader = ({view}: Types.WrappedViewState) =>
<div>
  <FlashMessage />
  <Breadcrumb.BreadcrumbComponent breadcrumbs={breadcrumbs}/>
  <Row className="margin-auto">
    <Col sm="12" md="10" className="text-left page-header--padding">
      <div className="page-header">
        <span className="title">Lead Posts</span>
        <AddLeadPostConnected />
      </div>
    </Col>
    <Col sm="12" md="2">
      <div className="page-header--padding">
        <IsActive.FilterSelect view={view} event={Constants.FETCH_LEAD_POSTS} disabled={view.isLoading}/>
      </div>
    </Col>
  </Row>
</div>

interface DispatchProps {
  dispatch: Redux.Dispatch<Redux.Action>,
  pageChange: Pager.ChangePageFunction,
}

interface DragCellProps {
  draggable: boolean,
}
type GridProps = Types.WrappedState & DispatchProps

const SmallAndMediumGrid = ({leadPosts, dispatch, pageChange}: GridProps) =>
<div>
  {leadPosts.data.length === 0
  ?
    <div>No posts loaded for this date</div>
  :
    <Grid data={Request.withDispatch(Pager.sliceState(leadPosts.data, leadPosts.page), dispatch)}
          resizable={true}
          sortable={{
            allowUnsort: true,
            mode: 'single',
          }}
          pageable={true}
          skip={leadPosts.page.skip}
          take={leadPosts.page.take}
          sort={leadPosts.view.sort}
          total={leadPosts.data.length}
          onPageChange={(e: GridPageChangeEvent) => dispatch(pageChange({
                                skip: e.page.skip,
                                take: e.page.take}))}
          onSortChange={(e: GridSortChangeEvent) => dispatch({
                                options: { value: e.sort },
                                type: Sortable.sortTableConstant(Constants.FETCH_LEAD_POSTS),
          })}>
      <GridColumn cell={Render.ActiveCell} field="is_active" title="Active" width={125}/>
      <GridColumn field="name" title="Source" width={200}/>
      <GridColumn cell={GridCell.PostTitleCell} field="title" title="Title" width={300}/>
      {PostsUtils.hasMultipleCategories(leadPosts.data) &&
      <GridColumn field="category_type" title="Category" width={100}/>}
    </Grid>}
  </div>

const SmallAndMediumGridConnected = connect(
  ({leadPosts}: Types.WrappedState) => ({leadPosts}),
  (dispatch) => ({
    dispatch,
    pageChange: Pager.changePage(Constants.FETCH_LEAD_POSTS),
  }),
)(SmallAndMediumGrid)

const isDragging = (data: Types.LeadPost[]): boolean => {
  return (data.filter((lp: Types.LeadPost) => lp.isDragging) || []).length > 0
}

type LargeGridProps = GridProps & DragCellProps
const LargeGrid = ({leadPosts, dispatch, draggable, pageChange}: LargeGridProps) =>
<div className={`no-border lead-posts-grid ${isDragging(leadPosts.data) && 'dragging'}`}>
  <Grid data={Request.withDispatch(Pager.sliceState(leadPosts.data, leadPosts.page), dispatch)}
        resizable={true}
        sortable={{
          allowUnsort: true,
          mode: 'single',
        }}
        pageable={true}
        selectedField="isDragging"
        skip={leadPosts.page.skip}
        take={leadPosts.page.take}
        sort={leadPosts.view.sort}
        total={leadPosts.data.length}
        onPageChange={(e: GridPageChangeEvent) => dispatch(pageChange({
                              skip: e.page.skip,
                              take: e.page.take}))}
        onSortChange={(e: GridSortChangeEvent) => dispatch({
                              options: { value: e.sort },
                              type: Sortable.sortTableConstant(Constants.FETCH_LEAD_POSTS),
        })}>
    {draggable && <GridColumn cell={Render.DragCell} field="is_active" title=" " width={75}/>}
    <GridColumn cell={Render.ActiveCell} field="is_active" title="Active" width={125}/>
    <GridColumn field="name" title="Source" width={200}/>
    <GridColumn cell={GridCell.PostTitleCell} field="title" title="Title" width={300}/>
    <GridColumn cell={Render.ImageCell} field="image_url" title="Image"width={100}/>
    <GridColumn cell={GridCell.TypeCell} field="subscription_type_sortable" title="Type" width={75}/>
    {PostsUtils.hasMultipleCategories(leadPosts.data) &&
    <GridColumn field="category_type" title="Category" width={75}/>}
    <GridColumn field="rank" title="Rank" width={75}/>
    <GridColumn cell={Render.DateCell} field="start_datetime" title="Run Date" width={130}/>
    <GridColumn cell={Render.DateCell} field="end_datetime" title="End Date" width={130}/>
    <GridColumn cell={GridCell.DateTimeCell} field="updated_datetime" title="Updated" width={150}/>
  </Grid>
</div>

const LargeGridConnected = connect(
  ({leadPosts}: Types.WrappedState) => ({leadPosts}),
  (dispatch) => ({
    dispatch,
    pageChange: Pager.changePage(Constants.FETCH_LEAD_POSTS),
  }),
)(LargeGrid)

type Props = GridProps & App.WrappedState & DragCellProps

export const LeadPostGrid = ({leadPosts, draggable, app}: Props) => <div>
  { leadPosts.view.isLoading
  ?
    <Loading center size={Loading.Sizes.LARGE} />
  :
    <div>
      <Row>
        <Col>
          {app.viewport.isLarge ? <LargeGridConnected draggable={draggable}/> : <SmallAndMediumGridConnected/>}
        </Col>
      </Row>
    </div>}
  </div>

export const LeadPostsGridConnected = connect(
  ({leadPosts, app}: Types.WrappedState & App.WrappedState) => ({leadPosts, app}),
  (dispatch) => ({
    dispatch,
    pageChange: Pager.changePage(Constants.FETCH_LEAD_POSTS),
  }),
)(LeadPostGrid)

const LeadPostsPage = ({leadPosts, app}: Types.WrappedState & App.WrappedState) => <div>
  <LeadPostHeader view={leadPosts.view}/>
  <LeadPostsGridConnected draggable={false} />
</div>

export const LeadPostsPageConnected = connect(
  ({leadPosts, app}: Types.WrappedState & App.WrappedState) => ({leadPosts, app}),
  (dispatch) => ({dispatch}),
)(LeadPostsPage)



// WEBPACK FOOTER //
// ./src/pages/lead-posts/component.tsx